import { useEffect, useState } from "react";
import { useAlerts } from "@/hooks";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { useGetOrgMetadataQuery } from "@/services/accountApi";
import { useAppContext } from "@/context/context";
import { IOrgMetadata } from "@/context/context.types";
import {
    useGetRoleManagementOrgQuery,
    useGetRoleManagementOrgUsersQuery,
    useLazyGetOrgMemberRolesQuery,
    useLazyGetOrgMemberPortfoliosQuery,
} from "../services/roleManagementApi";
import { parseUserToRow } from "../utils/parsers";
import { IAGRoleManagementDataRow } from "@pulse-ui/types/components/AGTable";

export const useRoleManagement = () => {
    const { showError } = useAlerts();
    const { update_org_metadata } = useAppContext();

    const { data: orgData, isLoading: isLoadingOrg, error: orgError } = useGetRoleManagementOrgQuery();
    const orgId = orgData?.org_id;

    // Populate context org_metadata so portfolio options are available in UserDetailsModal
    const { data: orgMetadata } = useGetOrgMetadataQuery(orgId ?? "", { skip: !orgId });

    useEffect(() => {
        if (orgMetadata) {
            update_org_metadata(orgMetadata as unknown as IOrgMetadata);
        }
    }, [orgMetadata]);

    const {
        data: usersData,
        isLoading: isLoadingUsers,
        error: usersError,
    } = useGetRoleManagementOrgUsersQuery(orgId ?? "", { skip: !orgId });

    const [getUserRoles] = useLazyGetOrgMemberRolesQuery();
    const [getUserPortfolios] = useLazyGetOrgMemberPortfoliosQuery();

    const [dataMap, setDataMap] = useState<Map<string, IAGRoleManagementDataRow>>(new Map());
    const [isEnriching, setIsEnriching] = useState(false);

    useEffect(() => {
        if (orgError) {
            showError(`Failed to load organization: ${getErrorMessage(orgError)}`);
        }
    }, [orgError, showError]);

    useEffect(() => {
        if (usersError) {
            showError(`Failed to load users: ${getErrorMessage(usersError)}`);
        }
    }, [usersError, showError]);

    useEffect(() => {
        if (!usersData || usersData.length === 0) {
            setDataMap(new Map());
            return;
        }

        setIsEnriching(true);

        const enrich = async () => {
            const enriched = await Promise.all(
                usersData.map(async (user: any) => {
                    const [rolesRes, portfoliosRes] = await Promise.all([
                        getUserRoles(user.org_user_id),
                        getUserPortfolios(user.org_user_id),
                    ]);
                    return parseUserToRow({
                        ...user,
                        roles: rolesRes.data ?? [],
                        portfolios: portfoliosRes.data ?? [],
                    });
                })
            );

            const newMap = new Map<string, IAGRoleManagementDataRow>(
                enriched.map((row) => [row.row_id as string, row])
            );
            setDataMap(newMap);
        };

        enrich()
            .catch(() => showError("Failed to load user roles and portfolios"))
            .finally(() => setIsEnriching(false));
    }, [usersData]);

    const loading = isLoadingOrg || isLoadingUsers || isEnriching;

    return {
        orgData,
        dataMap,
        loading,
    };
};
